import { Injectable } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import GeoJSONPoint from 'models/geojson-point/geojson-point.model';
import { loadModules } from 'esri-loader';
import EsriLoad from 'models/esri-load/esri-load.model';
import { ArcgisService } from '../arcgis/arcgis.service';
import Ticket from 'models/ticket/ticket.model';
import ExternalFeature from 'models/external-feature/external-feature.model';

@Injectable({
  providedIn: 'root',
})
export class ArcgisMapService {
  public esri: EsriLoad = new EsriLoad();
  clickedPosition: Subject<GeoJSONPoint<any>> = new Subject();
  clickedFeature: BehaviorSubject<ExternalFeature> = new BehaviorSubject(null);
  selectionEnabled: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public filteredLayer;
  pointsToShow: BehaviorSubject<GeoJSONPoint<{}>[]> = new BehaviorSubject([]);
  public possibleAttributes = [];

  constructor(public arcgisService: ArcgisService) { }

  public async init(): Promise<boolean> {
    return new Promise<boolean>(async (resolve: (r: boolean) => any) => {
      const [
        Map,
        Basemap,
        MapView,
        Graphic,
        GraphicsLayer,
        FeatureLayer,
        TileLayer,
        ImageryLayer,
        UnknownLayer,
        MapImageLayer,
        LayerList,
        SceneView,
        Editor,
        UrlUtils,
        IdentityManager,
        Expand,
        SpatialReference,
        WMSLayer,
      ] = await loadModules([
        'esri/Map',
        'esri/Basemap',
        'esri/views/MapView',
        'esri/Graphic',
        'esri/layers/GraphicsLayer',
        'esri/layers/FeatureLayer',
        'esri/layers/TileLayer',
        'esri/layers/ImageryLayer',
        'esri/layers/UnknownLayer',
        'esri/layers/MapImageLayer',
        'esri/widgets/LayerList',
        'esri/views/SceneView',
        'esri/widgets/Editor',
        'esri/core/urlUtils',
        'esri/identity/IdentityManager',
        'esri/widgets/Expand',
        'esri/geometry/SpatialReference',
        'esri/layers/WMSLayer',
      ]);
      this.esri.map = Map;
      this.esri.baseMap = Basemap;
      this.esri.mapView = MapView;
      this.esri.graphic = Graphic;
      this.esri.graphicsLayer = GraphicsLayer;
      this.esri.featureLayer = FeatureLayer;
      this.esri.tileLayer = TileLayer;
      this.esri.imageryLayer = ImageryLayer;
      this.esri.unknownLayer = UnknownLayer;
      this.esri.mapImageLayer = MapImageLayer;
      this.esri.layerList = LayerList;
      this.esri.sceneView = SceneView;
      this.esri.editor = Editor;
      this.esri.urlUtils = UrlUtils;
      this.esri.identityManager = IdentityManager;
      this.esri.expand = Expand;
      this.esri.spatialReference = SpatialReference;
      this.esri.wmslayer = WMSLayer;

      await this.esri.identityManager.initialize(this.esri.identityManager.toJSON());

      this.filteredLayer = new this.esri.graphicsLayer({ title: 'Összes' });
      return resolve(true);
    });
  }

  showFiltered(points: GeoJSONPoint<{ symbolType: string, ticket: Ticket }>[]) {
    this.filteredLayer.removeAll();
    const symbols = {
      selected: {
        strength: 2,
        type: 'simple-marker',
        color: [219, 148, 6],
        outline: {
          color: [0, 0, 0],
          width: 2,
        },
      },
      clicked: {
        strength: 3,
        type: 'simple-marker',
        color: [179, 0, 27],
        outline: {
          color: [0, 0, 0],
          width: 2,
        },
      },
      normal: {
        strength: 1,
        type: 'simple-marker',
        color: [155, 155, 155],
        outline: {
          color: [0, 0, 0],
          width: 1,
        },
      },
    };
    points.sort((b, a) => symbols[a.properties.symbolType].strength - symbols[b.properties.symbolType].strength);
    for (let i = 0; i < points.length; i++) {
      if (points[i].properties.ticket.externalFeatures.length !== 0) {
        continue;
      }
      const point = {
        type: 'point',
        y: points[i].geometry.coordinates[1],
        x: points[i].geometry.coordinates[0],
        spatialReference: {
          wkid: 23700,
        },
      };

      const pointGraphic = new this.esri.graphic({
        attributes: {
          ticket: points[i].properties.ticket,
        },
        geometry: point,
        symbol: symbols[points[i].properties.symbolType],
      });
      this.filteredLayer.add(pointGraphic);
    }
  }

  clearMap() {
    if (this.filteredLayer) {
      this.filteredLayer.removeAll();
    }
  }

  getClickedPosition(): Subject<GeoJSONPoint<any>> {
    return this.clickedPosition;
  }

  enableSelection() {
    this.selectionEnabled.next(true);
  }

  disableSelection() {
    this.selectionEnabled.next(false);
  }

  hasShownPoints() {
    return this.pointsToShow.value.length > 0;
  }
}
